import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Check from 'lucide-react/dist/esm/icons/check.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import Handshake from 'lucide-react/dist/esm/icons/handshake.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import { useParams, useSearchParams } from 'react-router-dom';
import usePartnerData from '../hooks/usePartnerData';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { useTranslations } from '../contexts/TranslationContext';
import { getConfig } from '../config/environment';
import { getSelectedPartner, invalidateSelectedPartner, storeSelectedPartner } from '../utils/partnerSelection';

const PARTNER_QUERY_KEYS = ['partnerId', 'partner', 'partner_id'];
const PARTNER_CODE_QUERY_KEYS = ['code', 'partnerCode', 'tlf_utm'];
const ORGANIZATION_QUERY_KEYS = ['organizationId', 'organization_id', 'orgId', 'org_id', 'OrgId', 'OrganizationId'];

const firstValue = (...values) => {
  for (const value of values) {
    if (value != null && value !== '') return String(value);
  }
  return '';
};

const readSearchValue = (searchParams, keys) => {
  for (const key of keys) {
    const value = searchParams.get(key);
    if (value) return String(value);
  }
  return '';
};

const extractOrganizationIdFromUrl = (url) => {
  if (!url) return '';

  try {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
    const parsedUrl = new URL(url, baseUrl);
    return readSearchValue(parsedUrl.searchParams, ORGANIZATION_QUERY_KEYS) || firstValue(parsedUrl.searchParams.get('id'));
  } catch {
    return '';
  }
};

const resolveOrganizationId = (siteDetails, routeParams, searchParams) => {
  const routeOrganizationId = firstValue(
    routeParams.organizationId,
    routeParams.organization_id,
    routeParams.orgId,
    routeParams.org_id,
    routeParams.OrgId,
    routeParams.OrganizationId
  );
  const searchOrganizationId = readSearchValue(searchParams, ORGANIZATION_QUERY_KEYS);
  const siteOrganizationId = firstValue(
    siteDetails?.organizationId,
    siteDetails?.organization_id,
    siteDetails?.orgId,
    siteDetails?.org_id,
    siteDetails?.OrganizationId,
    siteDetails?.OrgId,
    siteDetails?.organization?.id,
    siteDetails?.organization?.organizationId,
    siteDetails?.organization?.organization_id,
    siteDetails?.organization?.orgId,
    siteDetails?.organization?.org_id,
    siteDetails?.organization?.OrganizationId,
    siteDetails?.organization?.OrgId
  );
  const urlOrganizationId = firstValue(
    extractOrganizationIdFromUrl(siteDetails?.emailLoginUrl),
    extractOrganizationIdFromUrl(siteDetails?.facebookLoginUrl),
    extractOrganizationIdFromUrl(siteDetails?.googlePlusLoginUrl),
    extractOrganizationIdFromUrl(siteDetails?.appleLoginUrl),
    extractOrganizationIdFromUrl(siteDetails?.microsoftLoginUrl)
  );
  const { tlfId } = getConfig();

  return firstValue(routeOrganizationId, searchOrganizationId, siteOrganizationId, urlOrganizationId, tlfId);
};

const normalizePartner = (partner) => ({
  id: partner?.id == null ? '' : String(partner.id),
  code: firstValue(partner?.Code, partner?.code, partner?.partnerCode, partner?.PartnerCode),
  name: partner?.organization_name || partner?.organizationName || partner?.partner_name || partner?.partnerName || partner?.name || '',
  logoUrl: partner?.organization_logo_url || partner?.organizationLogoUrl || partner?.partner_logo_url || partner?.partnerLogoUrl || partner?.logo_url || partner?.logoUrl || null,
});

const normalizeMatchValue = (value) => String(value || '').trim().toLowerCase();

export function useHeroPartnerSelect() {
  const routeParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { siteDetails } = useSiteConfig() || {};
  const organizationId = useMemo(
    () => resolveOrganizationId(siteDetails, routeParams, searchParams),
    [routeParams, searchParams, siteDetails]
  );
  const canResolvePartners = !!organizationId;
  const { partners = [], fieldLabel = '', loading, required } = usePartnerData(null, canResolvePartners, organizationId, true, null, 'Hero');
  const normalizedPartners = useMemo(
    () => partners.map(normalizePartner).filter((partner) => partner && partner.id && (partner.name || partner.organization_name)),
    [partners]
  );
  const shouldRender =
    canResolvePartners &&
    !loading &&
    normalizedPartners.length > 0;

  // Persist the required-on-Hero flag so purchase CTAs across the page can
  // gate navigation without each layout re-fetching partner data.
  useEffect(() => {
    try {
      if (required) {
        sessionStorage.setItem('partnerRequiredOnHero', 'true');
      } else {
        sessionStorage.removeItem('partnerRequiredOnHero');
      }
    } catch {
      // sessionStorage may be unavailable in private browsing
    }
  }, [required]);

  return {
    routeParams,
    searchParams,
    setSearchParams,
    loading,
    normalizedPartners,
    shouldRender,
    fieldLabel,
    required,
  };
}

export default function HeroPartnerGlassSelect({
  className = '',
  label = 'Partner',
  placeholder = 'Choose a partner',
  autoAdvance = false,
  onRequestPurchase,
  purchasesBlocked = false,
}) {
  const { t: tr } = useTranslations();
  const controlId = useId();
  const selectedValueId = `${controlId}-value`;
  const listboxId = `${controlId}-listbox`;
  const rootRef = useRef(null);
  const optionRefs = useRef([]);
  const searchRef = useRef(null);
  const {
    routeParams,
    searchParams,
    setSearchParams,
    loading,
    normalizedPartners,
    shouldRender,
    fieldLabel,
    required,
  } = useHeroPartnerSelect();
  const { partnerId: routePartnerId, partnerCode: routePartnerCode } = routeParams;
  const [selectedId, setSelectedId] = useState(() => getSelectedPartner()?.id || '');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightRequired, setHighlightRequired] = useState(false);
  const [showAutoAdvanceModal, setShowAutoAdvanceModal] = useState(false);
  const [portalRoot, setPortalRoot] = useState(null);
  const useModal = normalizedPartners.length > 10;

  useEffect(() => {
    setPortalRoot(typeof document !== 'undefined' ? document.body : null);
  }, []);

  useEffect(() => {
    const handleFocusRequired = () => {
      if (!required) return;
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightRequired(true);
      const timer = setTimeout(() => setHighlightRequired(false), 1500);
      return () => clearTimeout(timer);
    };

    window.addEventListener('partner-required-focus', handleFocusRequired);
    return () => window.removeEventListener('partner-required-focus', handleFocusRequired);
  }, [required]);

  const urlPartnerValue = useMemo(() => firstValue(
    readSearchValue(searchParams, PARTNER_QUERY_KEYS),
    readSearchValue(searchParams, PARTNER_CODE_QUERY_KEYS),
    routePartnerId,
    routePartnerCode
  ), [routePartnerCode, routePartnerId, searchParams]);

  useEffect(() => {
    if (loading) return;

    if (!urlPartnerValue) {
      const storedPartner = getSelectedPartner();
      const matchedStoredPartner = storedPartner
        ? normalizedPartners.find((partner) => (
          (storedPartner.id && partner.id && normalizeMatchValue(partner.id) === normalizeMatchValue(storedPartner.id)) ||
          (storedPartner.code && partner.code && normalizeMatchValue(partner.code) === normalizeMatchValue(storedPartner.code))
        ))
        : null;
      if (matchedStoredPartner) {
        storeSelectedPartner(matchedStoredPartner);
      } else if (storedPartner && normalizedPartners.length > 0) {
        invalidateSelectedPartner(storedPartner);
      }
      setSelectedId(matchedStoredPartner?.id || '');
      return;
    }

    const normalizedUrlValue = normalizeMatchValue(urlPartnerValue);
    const matchedPartner = normalizedPartners.find((partner) => (
      (partner.id && normalizeMatchValue(partner.id) === normalizedUrlValue) ||
      (partner.code && normalizeMatchValue(partner.code) === normalizedUrlValue)
    ));
    if (matchedPartner) {
      storeSelectedPartner(matchedPartner);
      setSelectedId(matchedPartner.id);
    } else {
      if (normalizedPartners.length > 0) {
        invalidateSelectedPartner({ id: urlPartnerValue, code: urlPartnerValue });
      }
      setSelectedId('');
    }
  }, [loading, normalizedPartners, urlPartnerValue]);

  const selectedPartner = normalizedPartners.find((partner) => partner.id === selectedId);
  const isDisabled = loading || normalizedPartners.length === 0;
  const isMissingRequired = required && !selectedPartner;
  const requiredIndicator = required ? (
    <span className="ml-1 text-[10px] font-bold uppercase tracking-wider text-rose-300">*</span>
  ) : null;
  const selectedOptionIndex = selectedPartner
    ? normalizedPartners.findIndex((partner) => partner.id === selectedPartner.id) + 1
    : 0;
  const selectedValueText = selectedPartner?.name || (fieldLabel ? `Choose ${fieldLabel}` : placeholder);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && useModal && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
    if (!isOpen) setSearch('');
  }, [isOpen, useModal]);

  const focusOption = (index) => {
    optionRefs.current[index]?.focus();
  };

  const moveOptionFocus = (currentIndex, direction) => {
    const optionCount = normalizedPartners.length + 1;
    const nextIndex = (currentIndex + direction + optionCount) % optionCount;
    focusOption(nextIndex);
  };

  const handleSelect = (nextId) => {
    // When Hero is a required surface, do not allow the user to clear the
    // selection back to the placeholder "Choose" option.
    if (required && !nextId) return;

    setSelectedId(nextId);
    setIsOpen(false);

    const nextPartner = normalizedPartners.find((partner) => partner.id === nextId);
    storeSelectedPartner(nextPartner || null);

    const nextParams = new URLSearchParams(searchParams);
    PARTNER_QUERY_KEYS.forEach((key) => nextParams.delete(key));
    PARTNER_CODE_QUERY_KEYS.forEach((key) => nextParams.delete(key));
    if (nextId) nextParams.set('partnerId', nextId);
    setSearchParams(nextParams, { replace: true });

    if (required && nextId && autoAdvance && typeof onRequestPurchase === 'function') {
      setShowAutoAdvanceModal(true);
    }
  };

  const handleAutoAdvanceConfirm = () => {
    setShowAutoAdvanceModal(false);
    onRequestPurchase?.();
  };

  const handleAutoAdvanceCancel = () => {
    setShowAutoAdvanceModal(false);
  };

  const handleButtonKeyDown = (event) => {
    if (isDisabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      window.requestAnimationFrame(() => focusOption(selectedOptionIndex));
    }
  };

  const handleOptionKeyDown = (event, index, nextId) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveOptionFocus(index, 1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveOptionFocus(index, -1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusOption(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusOption(normalizedPartners.length);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleSelect(nextId);
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      <div
        ref={rootRef}
        className={`relative rounded-2xl border p-3 text-white shadow-2xl backdrop-blur-md transition-all duration-300 ${isOpen ? 'z-[80] overflow-visible' : 'overflow-hidden'} ${className} ${highlightRequired ? 'border-rose-400 bg-rose-500/15 shadow-[0_0_22px_-4px_rgba(244,63,94,0.55)]' : 'border-white/25 bg-white/10'}`}
        style={{
          boxShadow: '0 18px 50px -28px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.22)',
        }}
      >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/20 to-transparent" />
      <div className="relative flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/30 bg-white/15">
          {selectedPartner?.logoUrl ? (
            <img src={selectedPartner.logoUrl} alt="" className="h-full w-full object-contain bg-white/90 p-1" />
          ) : (
            <Handshake className="h-5 w-5 text-white/90" />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <span id={controlId} className="mb-1 block text-[10px] font-bold uppercase tracking-[0.14em] text-white/70">
            {fieldLabel || label}
            {requiredIndicator}
          </span>
          <div className="relative">
            <button
              type="button"
              aria-labelledby={`${controlId} ${selectedValueId}`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              disabled={isDisabled}
              onClick={() => setIsOpen((open) => !open)}
              onKeyDown={handleButtonKeyDown}
              className="flex w-full items-center justify-between gap-2 rounded-xl border border-white/25 bg-white/15 px-2.5 py-2 text-left text-sm font-bold text-white outline-none transition hover:bg-white/20 focus:border-white/60 focus:ring-2 focus:ring-white/35 disabled:cursor-not-allowed disabled:opacity-80"
            >
              <span id={selectedValueId} className="flex min-w-0 items-center gap-2">
                <span className={`flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${selectedPartner ? 'border-white/35 bg-white/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]' : 'border-white/25 bg-white/10'}`}>
                  {selectedPartner?.logoUrl ? (
                    <img src={selectedPartner.logoUrl} alt="" className="h-full w-full object-contain p-0.5" />
                  ) : (
                    <Handshake className={`h-3.5 w-3.5 ${selectedPartner ? 'text-slate-700' : 'text-white/80'}`} />
                  )}
                </span>
                <span className={`min-w-0 truncate ${selectedPartner ? 'text-white' : 'text-white/85'}`}>{selectedValueText}</span>
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-white/80 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMissingRequired && (
              <p className={`mt-2 flex items-center gap-1.5 text-xs font-medium transition-colors duration-300 ${highlightRequired ? 'text-rose-300' : 'text-white/80'}`}>
                <span className={`inline-block h-1.5 w-1.5 rounded-full transition-colors duration-300 ${highlightRequired ? 'bg-rose-400' : 'bg-white/60'}`} />
                {tr('partners.requiredOnHero', 'Please choose a {{fieldName}} to continue.').replace('{{fieldName}}', fieldLabel || label)}
              </p>
            )}

            {/* Inline dropdown — for ≤10 partners or desktop */}
            {isOpen && !isDisabled && !useModal && (
              <div
                id={listboxId}
                role="listbox"
                aria-labelledby={controlId}
                className="absolute left-0 right-0 top-full z-[90] mt-2 max-h-72 overflow-y-auto rounded-xl border border-white/25 bg-slate-950/[0.52] p-1.5 shadow-[0_28px_80px_-28px_rgba(0,0,0,0.92),inset_0_1px_0_rgba(255,255,255,0.24)] ring-1 ring-white/15 backdrop-blur-xl"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(255,255,255,0.16), rgba(15,23,42,0.62) 42%, rgba(2,6,23,0.78)), radial-gradient(circle at 16% 0%, rgba(255,255,255,0.22), transparent 34%)',
                }}
              >
                <OptionItem
                  ref={(element) => { optionRefs.current[0] = element; }}
                  isSelected={selectedId === ''}
                  onClick={() => handleSelect('')}
                  onKeyDown={(event) => handleOptionKeyDown(event, 0, '')}
                  label={fieldLabel ? `Choose ${fieldLabel}` : placeholder}
                />

                {normalizedPartners.map((partner, partnerIndex) => (
                  <OptionItem
                    key={partner.id}
                    ref={(element) => { optionRefs.current[partnerIndex + 1] = element; }}
                    isSelected={partner.id === selectedId}
                    onClick={() => handleSelect(partner.id)}
                    onKeyDown={(event) => handleOptionKeyDown(event, partnerIndex + 1, partner.id)}
                    logoUrl={partner.logoUrl}
                    label={partner.name}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      </div>

      {/* Modal dropdown — for >10 partners, fullscreen on mobile.
          Portaled to document.body so ancestor overflow/clip (e.g. the hero
          container) cannot constrain it. */}
      {isOpen && !isDisabled && useModal && portalRoot && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setIsOpen(false)}>
          <div
            className="w-full sm:max-w-md bg-gray-900 border-0 sm:border sm:border-white/15 rounded-none sm:rounded-2xl shadow-2xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[70vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/10">
              <div>
                <h3 className="text-base font-bold text-white">{fieldLabel ? `${tr('cinematic.choose', 'Choose')} ${fieldLabel}` : tr('cinematic.selectPartner', 'Select a Partner')}</h3>
                <p className="text-xs text-white/50 mt-0.5">
                  {tr('cinematic.optionsAvailable', '{{count}} options available for {{fieldName}}')
                    .replace('{{count}}', normalizedPartners.length)
                    .replace('{{fieldName}}', fieldLabel || tr('partners.fallbackName', 'partners'))}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-white/10">
              <div className="relative">
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={fieldLabel
                    ? tr('cinematic.searchField', 'Search {{fieldName}}...').replace('{{fieldName}}', fieldLabel)
                    : tr('cinematic.searchPartners', 'Search partners...')}
                  className="w-full bg-white/10 border border-white/15 rounded-xl px-4 py-2.5 pl-10 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 transition-colors"
                />
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                </svg>
              </div>
            </div>

            {/* Partner list */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-2 py-2">
              {!required && (
                <ModalOption
                  isSelected={selectedId === ''}
                  onClick={() => handleSelect('')}
                  label={fieldLabel ? `Choose ${fieldLabel}` : placeholder}
                />
              )}

              {filtered.length === 0 && (
                <p className="text-center text-sm text-white/40 py-6">{tr('cinematic.noMatch', 'No results for')} "{search}"</p>
              )}

              {filtered.map((partner) => (
                <ModalOption
                  key={partner.id}
                  isSelected={partner.id === selectedId}
                  onClick={() => handleSelect(partner.id)}
                  logoUrl={partner.logoUrl}
                  label={partner.name}
                />
              ))}
            </div>
          </div>
        </div>,
        portalRoot
      )}

      {/* Auto-advance confirmation — sticky bottom sheet on mobile.
          Portaled to document.body so it sits above everything else. */}
      {showAutoAdvanceModal && portalRoot && createPortal(
        <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4" onClick={handleAutoAdvanceCancel}>
          <div
            className="w-full sm:max-w-sm bg-gray-900 border-0 sm:border sm:border-white/15 rounded-t-2xl sm:rounded-2xl rounded-b-none shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--charity-primary, #dc2626)' }}>
                <Ticket className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">{tr('partners.autoAdvanceTitle', 'Ready to purchase?')}</h3>
                <p className="text-sm text-white/70 mt-1">
                  {tr('partners.autoAdvanceMessage', 'You selected {{partnerName}}. We\'ll take you to the ticket page.')
                    .replace('{{partnerName}}', selectedPartner?.name || '')}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={handleAutoAdvanceCancel}
                className="flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold text-white/80 bg-white/10 hover:bg-white/15 transition-colors"
              >
                {tr('partners.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={handleAutoAdvanceConfirm}
                disabled={purchasesBlocked}
                className="flex-1 rounded-xl px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-all active:scale-[0.98] disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, var(--charity-primary, #dc2626), var(--charity-secondary, #b91c1c))' }}
              >
                {tr('partners.continueToPurchase', 'Continue to Purchase')}
              </button>
            </div>
            {purchasesBlocked && (
              <p className="mt-3 text-xs text-center text-white/50">
                {tr('partners.purchasesBlocked', 'Ticket sales are currently paused.')}
              </p>
            )}
          </div>
        </div>,
        portalRoot
      )}
    </>
  );
}

const OptionItem = React.forwardRef(function OptionItem({ isSelected, onClick, onKeyDown, logoUrl, label }, ref) {
  return (
    <div
      ref={ref}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={`group flex cursor-pointer items-center gap-2 rounded-lg border px-2 py-2 text-sm font-semibold text-white outline-none transition hover:border-white/30 hover:bg-white/[0.13] focus:border-white/45 focus:bg-white/[0.16] focus:ring-2 focus:ring-white/25 ${isSelected ? 'border-white/40 bg-white/[0.18] shadow-[inset_0_1px_0_rgba(255,255,255,0.24)]' : 'border-white/0 bg-slate-950/[0.24]'}`}
    >
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-lg border ${isSelected ? 'border-white/45 bg-white/95' : 'border-white/25 bg-white/[0.14]'}`}>
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-full w-full object-contain p-0.5" />
        ) : (
          <Handshake className={`h-3.5 w-3.5 ${isSelected ? 'text-slate-700' : 'text-white/80'}`} />
        )}
      </span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition ${isSelected ? 'border-white/55 bg-white/25 text-white' : 'border-white/10 bg-white/5 text-white/0 group-hover:text-white/35 group-focus:text-white/60'}`}>
        <Check className="h-3 w-3" />
      </span>
    </div>
  );
});

function ModalOption({ isSelected, onClick, logoUrl, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-xl text-sm transition-colors hover:bg-white/10 flex items-center gap-3 mb-1 ${isSelected ? 'bg-white/10 text-white font-bold' : 'text-white/70'}`}
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg object-contain bg-white/90 p-1 shrink-0" />
      ) : (
        <div className="h-8 w-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
          <Handshake className="w-4 h-4 text-white/40" />
        </div>
      )}
      <span className="truncate">{label}</span>
      {isSelected && (
        <svg className="w-4 h-4 ml-auto shrink-0" style={{ color: 'var(--charity-primary, #ef4444)' }} fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
}
